import { ChartLineUpIcon } from "@phosphor-icons/react/dist/csr/ChartLineUp";
import { Fragment, useMemo, useState } from "react";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "@volca/ui/components/ui/empty";
import { PageTabsList } from "@volca/ui/components/ui/page-tabs-list";
import { Stack } from "@volca/ui/components/ui/stack";
import { Tabs, TabsTrigger } from "@volca/ui/components/ui/tabs";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { objectsOf } from "@/profitability/records/objects";
import { RecordObjectsProvider } from "@/profitability/records/objects-context";
import { useReportPresentation } from "@/profitability/report-presentation";
import type {
  ReportControlValues,
  ReportDates,
  ReportOutput,
} from "@/profitability/types";
import {
  bentoLayout,
  bentoOrder,
  isHeadlineFigure,
  isSupportingFigure,
} from "./bento-layout";
import { type Lens, PeriodLens } from "./period-lens";
import { ReportBlock } from "./report-block";
import { ReportControls } from "./report-controls";
import { reportPanes } from "./report-panes";
import { SupportingFigures } from "./supporting-figures";
import { controlChoices, currentValues } from "./utils";

export const ReportView = ({
  businessId,
  runId,
  output,
  dates,
  pane: controlledPane,
  onPaneChange,
  values,
  onValuesChange,
  lens,
  onLensChange,
}: {
  businessId: string;
  runId: string;
  output: ReportOutput;
  dates: ReportDates | null;
  /** Set by a page that shows the panes in its own bar. */
  pane?: string;
  onPaneChange?: (pane: string) => void;
  /**
   * The filters and the chosen day. Held by the page that shows the tabs,
   * because this view unmounts when the reader opens the grid and a filter
   * they set should still be set when they come back.
   */
  values: ReportControlValues;
  onValuesChange: (values: ReportControlValues) => void;
  lens: Lens;
  onLensChange: (lens: Lens) => void;
}) => {
  const classic = useReportPresentation() === "classic";
  const panes = useMemo(() => reportPanes(output), [output]);
  const objects = useMemo(() => objectsOf(output), [output]);
  const choices = useMemo(
    () => controlChoices(output.controls, output.datasets),
    [output.controls, output.datasets],
  );
  // Every reader below sees only the values that name a choice this run has.
  const current = useMemo(
    () => currentValues(values, choices),
    [values, choices],
  );
  const [ownPane, setOwnPane] = useState(panes[0]?.id);
  const pane = controlledPane ?? ownPane;
  const shown = panes.find((item) => item.id === pane) ?? panes[0];
  const ordered = useMemo(
    () => bentoOrder(shown?.blocks ?? []),
    [shown?.blocks],
  );
  // A tab offers the filters of what it draws: one on a dataset none of its
  // blocks shows would change nothing here.
  const paneControls = useMemo(() => {
    const drawn = new Set((shown?.blocks ?? []).map((block) => block.dataset));
    return output.controls.filter((control) => drawn.has(control.dataset));
  }, [output.controls, shown?.blocks]);
  // Supporting figures leave the grid and share one strip of their own, unless
  // they fit beside the headline figures: one supporting figure alone would
  // otherwise stretch across the page.
  const fitsOneRow =
    ordered.filter((block) => block.type === "summary").length <= 4;
  const placed =
    !classic && fitsOneRow
      ? ordered.map((block) =>
          isSupportingFigure(block)
            ? { ...block, emphasis: "headline" as const }
            : block,
        )
      : ordered;
  const supporting = placed.filter(isSupportingFigure);
  const blocks = placed.filter((block) => !isSupportingFigure(block));
  // The strip sits under the headline figures, above the charts. With no
  // headline figures it goes first, because there is nothing for it to sit
  // under and a report that declared only supporting figures still has to
  // show them.
  const headlines = blocks.filter(isHeadlineFigure).length;
  const strip = supporting.length ? (
    <SupportingFigures
      businessId={businessId}
      runId={runId}
      blocks={supporting}
      datasets={output.datasets}
      controls={output.controls}
      values={current}
      lens={lens}
    />
  ) : null;
  const layout = useMemo(
    () => bentoLayout(blocks, output.datasets),
    [blocks, output.datasets],
  );
  // What this report calls its headline figures, per dataset, so a row panel
  // leads with the same numbers the page does.
  const headline = useMemo(() => {
    const byDataset = new Map<string, string[]>();
    output.blocks.forEach((block) => {
      if (block.type !== "summary" || !isHeadlineFigure(block) || !block.field)
        return;
      const fields = byDataset.get(block.dataset) ?? [];
      if (!fields.includes(block.field)) fields.push(block.field);
      byDataset.set(block.dataset, fields);
    });
    return byDataset;
  }, [output.blocks]);
  return (
    <RecordObjectsProvider objects={objects}>
      <Stack className="min-h-0 w-full flex-1" gap={0} align="stretch">
        {panes.length > 1 && !onPaneChange && (
          <Tabs value={shown?.id} onValueChange={setOwnPane} className="pb-3">
            <PageTabsList>
              {panes.map((item) => (
                <TabsTrigger key={item.id} value={item.id}>
                  {item.title}
                </TabsTrigger>
              ))}
            </PageTabsList>
          </Tabs>
        )}
        <ReportControls
          controls={paneControls}
          datasets={output.datasets}
          choices={choices}
          values={current}
          onChange={onValuesChange}
          before={
            dates && (
              <PeriodLens
                dates={dates}
                datasets={output.datasets}
                value={lens}
                onChange={onLensChange}
              />
            )
          }
        />
        {!output.blocks.length ? (
          <Empty className={emptyStateVariants({ layout: "panel" })}>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <ChartLineUpIcon aria-hidden />
              </EmptyMedia>
              <EmptyDescription>
                This report has no results to show.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Stack
            gap={0}
            align="stretch"
            className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-12"
          >
            {!headlines && strip}
            {blocks.map((block, index) => (
              <Fragment key={block.id}>
                <ReportBlock
                  businessId={businessId}
                  runId={runId}
                  block={block}
                  dataset={
                    output.datasets.find(
                      (dataset) => dataset.id === block.dataset,
                    )!
                  }
                  controls={output.controls}
                  values={current}
                  lens={lens}
                  headline={headline.get(block.dataset)}
                  fill={blocks.length === 1}
                  placement={layout.get(block.id)!}
                  // A figure is a number, not a tile: it sits on the page
                  // like the Review's figures, and the charts and tables
                  // keep their cards.
                  variant={
                    !classic && block.type === "summary" ? "inline" : "card"
                  }
                />
                {!!headlines && index + 1 === headlines && strip}
              </Fragment>
            ))}
          </Stack>
        )}
      </Stack>
    </RecordObjectsProvider>
  );
};
